import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../../hooks/ApiProvider/ApiProvider';
import styles from './OrderDetail.module.css';

interface CouponUsage {
  user_id: string;
  user_name: string;
  user_email: string;
  user_phone: string;
  used_at: string;
}

interface CouponInfo {
  title?: string;
  id: string;
  code: string;
  name: string;
  description: string;
  valid_from: string;
  valid_until: string;
  status: string;
  giver_name?: string;
  giver_id?: string;

  usage?: CouponUsage;
}

interface OrderDetails {
  coupon?: CouponInfo;
}

interface OrderInfo {
  id: string;
  customer_id: string;
  customer_name?: string;
  product_id: string;
  order_name?: string;
  payment_state: string;
  payment_key?: string;
  amount?: number;
  is_gift?: boolean;
  phone_number?: string;
  created_at: string;
  updated_at?: string;
  details?: OrderDetails;
}

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getOrderDetail, deleteOrder } = useApi();
  
  const [order, setOrder] = useState<OrderInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrderDetail = async () => {
      if (!orderId) return;

      try {
        setLoading(true);
        setError(null);

        const orderData = await getOrderDetail(orderId);
        setOrder(orderData);
      } catch (err: any) {
        setError(err.message || '주문 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [orderId, getOrderDetail]);

  // 날짜 포맷팅
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 금액 포맷팅
  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ko-KR').format(amount) + '원';
  };

  // 결제 상태 배지
  const getPaymentStateBadge = (state: string) => {
    const stateMap: { [key: string]: { text: string; className: string; icon: string } } = {
      'ISSUED': { text: '대기', className: 'pending', icon: '⏳' },
      'PENDING': { text: '대기', className: 'pending', icon: '⏳' },
      'PAID': { text: '완료', className: 'paid', icon: '✅' },
      'CANCELLED': { text: '취소', className: 'cancelled', icon: '❌' },
      'REFUNDED': { text: '환불', className: 'refunded', icon: '↩️' },
      'FAILED': { text: '실패', className: 'failed', icon: '⚠️' },
      'REJECTED': { text: '거부', className: 'failed', icon: '🚫' }
    };
    
    const stateInfo = stateMap[state] || { text: state, className: 'unknown', icon: '❓' };
    
    return (
      <span className={styles.paymentStateIcon}>
        <span className={styles[stateInfo.className as keyof typeof styles]}>{stateInfo.icon}</span>
        <span className={styles.paymentStateText}>{stateInfo.text}</span>
      </span>
    );
  };

  // 선물 여부 표시
  const getGiftBadge = (isGift?: boolean) => {
    if (isGift === undefined || isGift === null) return '-';
    
    return (
      <span className={styles.giftTypeIcon}>
        <span className={isGift ? styles.gift : styles.normal}>{isGift ? '🎁' : '📦'}</span>
        <span className={styles.giftTypeText}>{isGift ? '선물' : '일반'}</span>
      </span>
    );
  };

  // 쿠폰 상태 배지
  const getCouponStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { text: string; className: string; icon: string } } = {
      'ACTIVE': { text: '활성', className: 'active', icon: '✅' },
      'INACTIVE': { text: '비활성', className: 'inactive', icon: '⏸️' },
      'EXPIRED': { text: '만료', className: 'expired', icon: '⏰' }
    };
    
    const statusInfo = statusMap[status] || { text: status, className: 'inactive', icon: '❓' };
    
    return (
      <span className={`${styles.couponStatusBadge} ${styles[statusInfo.className as keyof typeof styles]}`}>
        {statusInfo.icon} {statusInfo.text}
      </span>
    );
  };

  // 주문 삭제
  const handleDeleteOrder = async (orderId: string) => {
    if (window.confirm('정말로 이 주문을 삭제하시겠습니까?\n\n⚠️ 이 작업은 되돌릴 수 없습니다.')) {
      try {
        const response = await deleteOrder(orderId);
        if (response.success) {
          alert('✅ 결제 정보가 성공적으로 삭제되었습니다.');
          handleGoBack();
        } else {
          alert('❌ 결제 정보 삭제에 실패했습니다.\n\n이미 이용되고 있는 결제 정보는 삭제할 수 없습니다.');
        }
      } catch (error) {
        alert('❌ 삭제 중 오류가 발생했습니다.');
      }
    }
  };

  // 이전 페이지 정보 가져오기
  const fromPage = location.state?.from;
  const fromPageName = location.state?.fromName;

  // 뒤로가기 처리
  const handleGoBack = () => {
    if (fromPage && fromPage !== '/admin/orders') {
      // 다른 페이지에서 온 경우 해당 페이지로 이동
      navigate(fromPage);
    } else {
      // 주문 목록에서 온 경우 전달받은 페이지 정보로 이동
      const state = location.state;
      if (state && state.currentPage) {
        // URL 파라미터로 페이지 정보 전달
        const params = new URLSearchParams();
        params.set('page', state.currentPage.toString());
        params.set('pageSize', state.pageSize.toString());
        
        // 검색 필터 정보 추가
        if (state.searchFilters) {
          Object.entries(state.searchFilters).forEach(([key, value]) => {
            if (value && value !== '') {
              params.set(key, value.toString());
            }
          });
        }

        // 정렬 조건 정보 추가
        if (state.sortConditions && state.sortConditions.length > 0) {
          const sortParams = state.sortConditions.map((sort: any) => `${sort.field}:${sort.order}`);
          params.set('sort', sortParams.join(','));
        }

        navigate(`/admin/orders?${params.toString()}`);
      } else {
        // 페이지 정보가 없는 경우 기본값
        navigate('/admin/orders');
      }
    }
  };

  // 뒤로가기 버튼 텍스트 결정
  const getBackButtonText = () => {
    if (fromPage && fromPage !== '/admin/orders') {
      return `← ${fromPageName || '이전 페이지'}로 돌아가기`;
    }
    return '← 목록으로 돌아가기';
  };

  if (loading) {
    return (
      <div className={styles.orderDetail}>
        <div className={styles.pageTitleRow}>
          <h1>결제 상세 정보</h1>
        </div>
        <div className={styles.loadingSpinner}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
          로딩 중...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.orderDetail}>
        <div className={styles.pageTitleRow}>
          <h1>결제 상세 정보</h1>
        </div>
        <div className={styles.errorMessage}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
          {error}
          <button onClick={handleGoBack} className={styles.btnBack}>
            {getBackButtonText()}
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className={styles.orderDetail}>
        <div className={styles.pageTitleRow}>
          <h1>결제 상세 정보</h1>
        </div>
        <div className={styles.errorMessage}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>🔍</div>
          주문 정보를 찾을 수 없습니다.
          <button onClick={handleGoBack} className={styles.btnBack}>
            {getBackButtonText()}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.orderDetail}>
      <div className={styles.pageTitleRow}>
        <h1>결제 상세 정보</h1>
        <button onClick={handleGoBack} className={styles.btnBack}>
          {getBackButtonText()}
        </button>
      </div>
      
      <div className={styles.orderDetailContent}>
        {/* 주문 기본 정보 섹션 */}
        <div className={styles.orderInfoSection}>
          <h2>📋 결제 기본 정보</h2>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>주문 ID</label>
              <span>{order.id}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문자명</label>
              <span>{order.customer_name || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>상품 ID</label>
              <span>{order.product_id}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문명</label>
              <span>{order.order_name || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>결제 상태</label>
              <span>{getPaymentStateBadge(order.payment_state)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>결제 키</label>
              <span>{order.payment_key || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문 금액</label>
              <span style={{ fontWeight: '600', color: '#059669' }}>{formatAmount(order.amount)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문 유형</label>
              <span>{getGiftBadge(order.is_gift)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>전화번호</label>
              <span>{order.phone_number || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>주문일</label>
              <span>{formatDate(order.created_at)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>수정일</label>
              <span>{order.updated_at ? formatDate(order.updated_at) : '-'}</span>
            </div>
          </div>
        </div>

        {/* 쿠폰 정보 섹션 */}
        {order.details?.coupon && (
          <div className={styles.couponInfoSection}>
            <h2>
              🎫 쿠폰 정보 : 
              <span 
                style={{
                  color: order.details.coupon.title?.includes('받은') ? '#dc2626' : '#059669',
                  fontWeight: '700',
                  fontSize: '0.95em',
                  backgroundColor: order.details.coupon.title?.includes('받은') ? '#fef2f2' : '#f0fdf4',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  marginLeft: '6px',
                  border: `1px solid ${order.details.coupon.title?.includes('받은') ? '#f87171' : '#10b981'}`,
                  boxShadow: `0 1px 3px ${order.details.coupon.title?.includes('받은') ? 'rgba(220, 38, 38, 0.1)' : 'rgba(5, 150, 105, 0.1)'}`,
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }}
              >
                {order.details.coupon.title}
              </span>
            </h2>
            <div className="w-full flex flex-col gap-2 p-3">
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <label>선물한 사람</label>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => navigate(`/admin/users/detail/${order.details?.coupon?.giver_id}`, {
                        state: { 
                          from: location.pathname,
                          fromName: '주문 상세 정보'
                        }
                      })}
                      style={{
                        color: '#3b82f6',
                        textDecoration: 'none',
                        fontWeight: '500',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        font: 'inherit'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      <div className="flex flex-row gap-1 justify-between w-full">
                        <div>
                          {`${order.details.coupon.giver_name}`}
                        </div>
                        <div className="text-right">
                          {/* 상세히 보기로 가기 아이콘 */}
                          사용자 정보 보기
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.infoGrid}>
              </div>

              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <label>쿠폰</label>
                  <span style={{ fontFamily: 'monospace', fontWeight: '600' }}>{order.details.coupon.code}</span>
                  <span>{order.details.coupon.name} ({order.details.coupon.description})</span>
                </div>
                <div className={styles.infoItem}>
                  <label>유효 기간</label>
                  <span>시작: {`${formatDate(order.details.coupon.valid_from)}`}</span> 
                  <span>만료: {`${formatDate(order.details.coupon.valid_until)}`}</span>
                </div>

                <div className={styles.infoItem}>
                  <label>쿠폰 상태</label>
                  <span>{getCouponStatusBadge(order.details.coupon.status)}</span>
                </div>
                {order.details.coupon.usage && (
                  <div className={styles.infoItem}>
                    <label>쿠폰 사용자</label>
                    <span>
                      <button
                        onClick={() => navigate(`/admin/users/${order.details?.coupon?.usage?.user_id}`, {
                          state: { 
                            from: location.pathname,
                            fromName: '주문 상세 정보'
                          }
                        })}
                        style={{
                          color: '#3b82f6',
                          textDecoration: 'none',
                          fontWeight: '500',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          font: 'inherit'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = 'none';
                        }}
                      >
                        {`${order.details.coupon.usage.user_name} (${order.details.coupon.usage.user_email})`}
                      </button>
                    </span>
                  </div>
                )}

                <div className={styles.infoItem}>
                  <label>쿠폰 사용일</label>
                  <span>{order.details.coupon.usage ? formatDate(order.details.coupon.usage.used_at) : '-'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 주문 상태 변경 섹션 */}
        <div className={styles.orderActionsSection}>
          <h2>⚙️ 결제 정보 관리</h2>
          <div className={styles.actionButtons + " justify-end"}>
            {/* 환불 처리 버튼 - 조건에 따라 활성화/비활성화 */}
            <button
              className={styles.btnRefund}
              disabled={order.payment_state === 'REFUNDED' || (order.amount || 0) <= 0}
              onClick={() => navigate(`/admin/orders/refund/${order.id}`, {
                state: { from: location.pathname }
              })}
              title={
                order.payment_state === 'REFUNDED' 
                  ? '이미 환불 처리된 주문입니다' 
                  : (order.amount || 0) <= 0 
                    ? '환불할 금액이 없습니다' 
                    : '환불 처리를 진행합니다'
              }
            >
              💸 환불 처리
            </button>

            <button
              className={styles.btnEdit}
              onClick={() => navigate(`/admin/orders/edit/${order.id}`, {
                state: { from: location.pathname }
              })}
            >
              ✏️ 결제 정보 수정
            </button>
            <button
              className={styles.btnDelete}
              onClick={() => handleDeleteOrder(order.id)}
            >
              🗑️ 결제 정보 삭제
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetail;
